import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, getDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { User } from '../../types/labeling';

export default function WorkforceDashboard() {
  const [users, setUsers] = useState<User[]>([]);
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Workforce — Pixano';
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load users
        const usersQuery = query(collection(db, 'users'), orderBy('role'));
        const usersSnapshot = await getDocs(usersQuery);
        const usersData = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as User[];
        setUsers(usersData);

        // Load today's metrics
        const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const metricsDoc = await getDoc(doc(db, 'metrics_daily', today));
        if (metricsDoc.exists()) {
          setMetrics(metricsDoc.data());
        }

        setLoading(false);
      } catch (error) {
        console.error('Error loading workforce data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const toggleUserActive = async (userId: string, active: boolean) => {
    try {
      await updateDoc(doc(db, 'users', userId), { active });
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, active } : user
      ));
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user status');
    }
  };

  const updateMaxWip = async (userId: string, maxWip: number) => {
    try {
      await updateDoc(doc(db, 'users', userId), { maxWip });
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, maxWip } : user
      ));
    } catch (error) {
      console.error('Error updating max WIP:', error);
      alert('Error updating max WIP');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Workforce Dashboard</h1>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading workforce data...</p>
        </div>
      </div>
    );
  }

  const annotators = users.filter(u => u.role === 'annotator');
  const activeAnnotators = annotators.filter(u => u.active);
  const totalWip = annotators.reduce((sum, u) => sum + (u.maxWip || 3), 0);

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Workforce Dashboard</h1>
      
      {/* Metrics Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold">Q</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Queue Size</p>
              <p className="text-2xl font-semibold text-gray-900">
                {metrics?.queue ? Object.values(metrics.queue as Record<string, number>).reduce((a: number, b: number) => a + b, 0) : 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-semibold">A</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Annotators</p>
              <p className="text-2xl font-semibold text-gray-900">{activeAnnotators.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-yellow-600 font-semibold">W</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total WIP Capacity</p>
              <p className="text-2xl font-semibold text-gray-900">{totalWip}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 font-semibold">S</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">SLA Breaches</p>
              <p className="text-2xl font-semibold text-gray-900">
                {metrics?.sla_breaches || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Queue by Priority */}
      {metrics?.queue && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Queue by Priority</h3>
          <div className="grid grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map(priority => (
              <div key={priority} className="text-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 ${
                  priority >= 4 ? 'bg-red-100' :
                  priority >= 3 ? 'bg-yellow-100' :
                  'bg-gray-100'
                }`}>
                  <span className={`font-semibold ${
                    priority >= 4 ? 'text-red-600' :
                    priority >= 3 ? 'text-yellow-600' :
                    'text-gray-600'
                  }`}>
                    P{priority}
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {metrics.queue[`p${priority}`] || 0}
                </p>
                <p className="text-sm text-gray-500">jobs</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Annotators Table */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Annotators</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  WIP Limit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Skills
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Assigned
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {annotators.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {user.id}
                    </div>
                    <div className="text-sm text-gray-500">
                      {user.role}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={user.maxWip || 3}
                      onChange={(e) => updateMaxWip(user.id, parseInt(e.target.value))}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.skills?.join(', ') || 'None'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.lastAssignedAt 
                      ? new Date(user.lastAssignedAt.seconds * 1000).toLocaleDateString()
                      : 'Never'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => toggleUserActive(user.id, !user.active)}
                      className={`px-3 py-1 rounded text-xs ${
                        user.active
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {user.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


